import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncSectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Gives a single card/component its own independent { data, loading, error, retry }
 * lifecycle, decoupled from every other card on the same tab.
 *
 * Why this exists: several Ticketing Analytics tabs used to fetch every card's data
 * in one `Promise.all` and share one `loading`/`error` flag for the whole tab. That
 * meant a single failing endpoint (e.g. the Aging Tickets API) blanked out the
 * entire tab instead of just the one card that depends on it.
 *
 * Usage: call once per card with a memoized fetcher and its dependency array
 * (typically the applied filters/time period). The hook re-fetches on mount and
 * whenever `deps` change, and re-runs only the given `fetcher` on `retry()` —
 * never any other card's request.
 */
export interface UseAsyncSectionOptions {
  /**
   * When false, the section neither fetches nor touches existing state —
   * used to keep a tab's cards lazy (only fetch once that tab is active),
   * matching the project's existing per-tab fetch-on-activate behavior.
   * Defaults to true.
   */
  enabled?: boolean;
  toFriendlyError?: (err: unknown) => string;
}

export function useAsyncSection<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  options?: UseAsyncSectionOptions,
): AsyncSectionState<T> {
  const { enabled = true, toFriendlyError } = options ?? {};
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // Monotonically increasing token: only the most recent request is allowed
  // to commit its result, so a stale in-flight request (e.g. from filters
  // that already changed again) can never overwrite newer data.
  const requestIdRef = useRef(0);
  const isLoadingRef = useRef(false);

  const run = useCallback(() => {
    const requestId = ++requestIdRef.current;
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    fetcherRef.current()
      .then((result) => {
        if (requestIdRef.current !== requestId) return;
        setData(result);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        const message = toFriendlyError ? toFriendlyError(err) : (err?.message ?? 'Failed to load data.');
        setError(message);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        isLoadingRef.current = false;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Not this section's turn (e.g. its tab isn't active yet) — leave any
    // previously-fetched data/error untouched and skip the network call.
    if (!enabled) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  const retry = useCallback(() => {
    // Prevents duplicate concurrent requests from rapid Retry clicks, while
    // still allowing the effect above to start a fresh request whenever the
    // section's own deps (filters/time period) change.
    if (isLoadingRef.current) return;
    run();
  }, [run]);

  return { data, loading, error, retry };
}
